import React, { useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import ReactPlayer from 'react-player';
import { useLocation } from "react-router-dom";

const PlayerStatus = {
  PENDING: 'PENDING',
  ALLOWED: 'ALLOWED',
  BLOCKED: 'BLOCKED',
};

const AutoplayVideoPlayer = ({
  url,
  fallbackImage,
  className = '',
  onStatusChange,
  containerStyle = {},
  playerStyle = {},
  onReady,
  onError

}) => {
  const location = useLocation();
  // Get lowPowerModeStatus from Redux instead of props
  const lowPowerModeStatus = useSelector(state => state.device.lowPowerModeStatus);
  const playerRef = useRef(null);

  // Use low power mode status from parent service
  const shouldBlockVideo = useMemo(() => {
    // If lowPowerModeStatus is null, we're still detecting, so don't block video by default
    if (!lowPowerModeStatus) {
      //console.log('Low power mode detection not complete, allowing video by default');
      return false;
    }
    
    // For iPhone: block video ONLY if low power mode is explicitly detected
    if (lowPowerModeStatus.deviceType === 'iPhone') {
      // Block only if we explicitly detected low power mode as true
      if (lowPowerModeStatus.isLowPowerMode === true) {
        //console.log('iPhone in low power mode - blocking video:', lowPowerModeStatus);
        return true;
      }
      // Otherwise (normal mode or uncertain), allow video
      //console.log('iPhone not in low power mode - allowing video:', lowPowerModeStatus);
      return false;
    }
    
    // For non-iPhone iOS devices, block only when explicitly in low power mode
    if (lowPowerModeStatus.isIOSDevice && lowPowerModeStatus.isLowPowerMode === true) {
      //console.log('Video blocked due to iOS device in low power mode:', lowPowerModeStatus);
      return true;
    }
    
    // All other cases: allow video (non-iOS devices, iOS not in low power mode, etc.)
    //console.log('Video allowed - not iOS low power mode combination:', lowPowerModeStatus);
    return false;
  }, [lowPowerModeStatus]);

  // Handle video blocking based on low power mode status from parent service
  useEffect(() => {
    if (shouldBlockVideo) {
      //console.log('Showing fallback image for iOS device in low power mode');
      onStatusChange?.(PlayerStatus.BLOCKED);
      return;
    }

    // Not iOS low power mode combination, proceed with normal video playback
   // console.log('Not iOS low power mode combination, proceeding with video playback');
  }, [shouldBlockVideo, onStatusChange]);

  // Test video autoplay capability
  useEffect(() => {
    if (shouldBlockVideo) {
      return;
    }

    const testVideo = document.createElement('video');
    testVideo.muted = true;
    testVideo.playsInline = true;
    testVideo.src = url;

    const playPromise = testVideo.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          onStatusChange?.(PlayerStatus.ALLOWED);
        })
        .catch(() => {
          onStatusChange?.(PlayerStatus.BLOCKED);
        });
    }

    return () => {
      testVideo.pause();
      testVideo.removeAttribute('src');
      testVideo.load();
    };
  }, [shouldBlockVideo, url, onStatusChange]);

  // Always render both elements with smooth transitions
  // Conservative approach: Show fallback for iPhone devices in low power mode
  const shouldShowFallback = useMemo(() => {
    // If lowPowerModeStatus is not available yet, don't show fallback by default
    if (!lowPowerModeStatus) {
      return false;
    }
    
    // For iPhone: show fallback ONLY if low power mode is explicitly detected as true
    if (lowPowerModeStatus.deviceType === 'iPhone') {
      // Only show fallback if we're explicitly sure it's in low power mode
      return lowPowerModeStatus.isLowPowerMode === true;
    }
    
    // For other iOS devices: show fallback only when explicitly in low power mode
    return Boolean(lowPowerModeStatus.isIOSDevice && lowPowerModeStatus.isLowPowerMode === true);
  }, [lowPowerModeStatus]);

  // // Debug logging for iPhone low power mode
  // useEffect(() => {
  //   console.log('=== AutoplayVideoPlayer Debug ===');
  //   console.log('lowPowerModeStatus:', lowPowerModeStatus);
  //   console.log('shouldShowFallback:', shouldShowFallback);
  //   console.log('shouldBlockVideo:', shouldBlockVideo);
  //   if (lowPowerModeStatus) {
  //     console.log('isIOSDevice:', lowPowerModeStatus.isIOSDevice);
  //     console.log('isLowPowerMode:', lowPowerModeStatus.isLowPowerMode);
  //     console.log('deviceType:', lowPowerModeStatus.deviceType);
  //   }
  //   console.log('================================');
  // }, [lowPowerModeStatus, shouldShowFallback, shouldBlockVideo]);

  const containerStyles = useMemo(() => ({
    position: 'fixed',
    width: '100vw',
    height: '100vh',
    zIndex: -1,
    overflow: 'hidden',
    ...containerStyle,
  }), [containerStyle]);

  const fallbackStyles = useMemo(() => ({
    backgroundImage: `url(${fallbackImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: shouldShowFallback ? 1 : 0,
    transition: 'opacity 0.8s ease-in-out',
    zIndex: -2,
  }), [fallbackImage, shouldShowFallback]);

  const videoStyles = useMemo(() => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: shouldShowFallback ? 0 : 1,
    transition: 'opacity 0.8s ease-in-out',
    zIndex: -1,
    ...playerStyle,
  }), [playerStyle, shouldShowFallback]);

  return (
    <div
      style={containerStyles}
      className={className}
    >
      {/* Debug Panel for iPhone Testing - Only in development */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'absolute',
          top: '40px',
          right: '20px',
          background: 'rgba(0,0,0,0.9)',
          color: 'white',
          padding: '10px',
          fontSize: '11px',
          zIndex: 1000,
          borderRadius: '5px',
          maxWidth: '250px',
          fontFamily: 'monospace'
        }}>
          <div>Device: {lowPowerModeStatus?.deviceType || 'Detecting...'}</div>
          <div>iOS: {lowPowerModeStatus?.isIOSDevice ? 'Yes' : 'No'}</div>
          <div>Low Power: {lowPowerModeStatus?.isLowPowerMode ? 'Yes' : 'No'}</div>
          <div>Show Fallback: {shouldShowFallback ? 'Yes' : 'No'}</div>
          <div>iPhone UA: {navigator.userAgent.includes('iPhone') ? 'Yes' : 'No'}</div>
          <div>Reduced Motion: {window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'Yes' : 'No'}</div>
        </div>
      )} */}
      {/* Fallback image - always rendered */}
      <div
        style={{
          backgroundImage: `url(${fallbackImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          position: 'absolute',
          width: '100%',
          height: '100%',
          opacity: shouldShowFallback ? 1 : 0,
          transition: 'opacity 0.8s ease-in-out',
          zIndex: -2,
        }}
        role="img"
        aria-label="Background image when video cannot play"
      />
      
      {/* Video player - only rendered when playback is allowed */}
      { !shouldBlockVideo && (
        <ReactPlayer
          ref={playerRef}
          url={url}
          className="video-player"
          playing={true}
          loop={true}
          muted={true}
          playsinline={true}
          controls={false}
          width="100%"
          height="100%"
          style={videoStyles}
          config={{
            file: {
              attributes: {
                playsInline: true,
                controls: false,
                autoPlay: true,
                muted: true,
                'webkit-playsinline': 'true',
                'x5-video-player-type': 'h5',
                'x5-video-player-fullscreen': 'false',
                poster: fallbackImage,
                preload: 'auto',
              },
              forceVideo: true,
            },
          }}
          onReady={() => {
            onReady?.();
          }}
          onStart={() => {
            // console.log('Video started playing');
          }}
          onPlay={() => {
            // console.log('Video playing');
          }}
          onError={(e) => {
            onStatusChange?.(PlayerStatus.BLOCKED);
            onError?.(e);
          }}
        />
      )}
    </div>
  );
};

export default React.memo(AutoplayVideoPlayer);